import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Archive, Download, TriangleAlert } from "lucide-react";
import { toast } from "sonner";
import { usePOS } from "@/context/POSContext";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const describeError = error => error?.response?.data?.detail || error?.message || "Request failed";
const sizeText = bytes => (bytes >= 1048576 ? `${(bytes / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);
const whenText = iso => new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });

/**
 * Database backups (owner decision list #9, built 2026-11-01). The server writes a
 * full dump to a folder on the restaurant's own machine and keeps the newest 30;
 * each one can be downloaded to keep somewhere else. Restoring is deliberately not
 * a button: it replaces every bill, and is done by hand with the till closed.
 */
export default function BackupsPanel() {
  const { token } = usePOS();
  const auth = { headers: token ? { Authorization: `Bearer ${token}` } : {} };
  const [list, setList] = useState({ phase: "loading", rows: [] });
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/backups`, auth);
      setList({ phase: "ready", rows: data });
    } catch (error) {
      setList({ phase: "error", rows: [], reason: describeError(error) });
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const backUpNow = async () => {
    setBusy("create");
    try {
      const { data } = await axios.post(`${API}/admin/backup`, {}, auth);
      toast.success(`Backup saved on the server: ${data.filename} (${sizeText(data.size_bytes)})`);
      await load();
    } catch (error) {
      toast.error(`Backup not taken — ${describeError(error)}`);
    } finally {
      setBusy("");
    }
  };

  const download = async filename => {
    setBusy(filename);
    try {
      const response = await axios.get(`${API}/admin/backups/${filename}`, { ...auth, responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(`Could not download ${filename} — ${describeError(error)}`);
    } finally {
      setBusy("");
    }
  };

  return <section className="bg-card border rounded-2xl shadow-sm overflow-hidden" data-testid="backups-panel">
    <header className="p-6 border-b flex flex-col sm:flex-row sm:items-start gap-4 bg-muted/20">
      <span className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
        <Archive className="w-5 h-5"/>
      </span>
      <div className="flex-1">
        <h3 className="text-lede font-semibold text-foreground">Database backups</h3>
        <p className="text-sm text-muted-foreground font-medium max-w-2xl mt-0.5">
          A full copy of every bill, order, menu and setting, saved on the restaurant's server. The newest 30 are kept.
          Download one to keep a copy off this machine.
        </p>
      </div>
      <Button variant="action" className="h-11" disabled={!!busy} onClick={backUpNow} data-testid="backup-now-button">
        {busy === "create" ? "Backing up…" : "Back up now"}
      </Button>
    </header>

    {list.phase === "error" && (
      <p className="p-4 text-sm text-destructive flex items-center gap-2" role="alert" data-testid="backups-error">
        <TriangleAlert className="w-4 h-4 shrink-0"/>Could not list the backups: {list.reason}
      </p>
    )}
    {list.phase === "ready" && list.rows.length === 0 && (
      <p className="p-4 text-sm text-muted-foreground" data-testid="backups-empty">No backups yet.</p>
    )}
    <ul className="divide-y" data-testid="backups-list">
      {list.rows.map(row => (
        <li key={row.filename} className="p-4 flex items-center justify-between gap-4">
          <div className="flex flex-col min-w-0">
            <strong className="text-body font-semibold">{whenText(row.created_at)}</strong>
            <small className="text-xs text-muted-foreground truncate">{row.filename} · {sizeText(row.size_bytes)}</small>
          </div>
          <Button variant="outline" className="h-11" disabled={!!busy} onClick={() => download(row.filename)}
                  data-testid={`backup-download-${row.filename}`}>
            <Download className="w-4 h-4 mr-1.5"/>{busy === row.filename ? "Downloading…" : "Download"}
          </Button>
        </li>
      ))}
    </ul>
  </section>;
}
